import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { collection, getDocs, updateDoc, doc } from 'firebase/firestore';
import { db } from '../firebase/config';
import { useCart } from '../context/CartContext';
import { soundEffects } from '../utils/soundEffects';
import { generateProductSlug, getProductPathPrefix } from '../utils/seoUtils';
import ProductCard from '../components/ProductCard';
import Products2Hero from '../components/Products2Hero';
import './LaptopsPage.css'; // Reusing styles from LaptopsPage

const AllProductsPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { addToCart } = useCart();
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedBrand, setSelectedBrand] = useState('');

  // Filter states
  const [filters, setFilters] = useState({
    brand: '',
    priceRange: '',
    category: ''
  });
  
  const [showFilters, setShowFilters] = useState(false);
  const [sortBy, setSortBy] = useState('featured');

  useEffect(() => {
    fetchAllProducts();
  }, []);

  useEffect(() => {
    // Check for category and brand parameters in URL
    const urlParams = new URLSearchParams(location.search);
    const categoryParam = urlParams.get('category');
    const brandParam = urlParams.get('brand');
    
    if (categoryParam) {
      setSelectedCategory(categoryParam);
      setFilters(prev => ({
        ...prev,
        category: categoryParam
      }));
    }
    
    if (brandParam) {
      setSelectedBrand(brandParam);
      setFilters(prev => ({
        ...prev,
        brand: brandParam
      }));
    }
  }, [location.search]);

  useEffect(() => {
    applyFilters();
  }, [products, filters, sortBy]);

  const fetchAllProducts = async () => {
    try {
      setLoading(true);
      const productCategories = ['laptops', 'monitors', 'desktops', 'minipc', 'all-in-one', 'accessories'];
      const allProductData = [];

      for (const category of productCategories) {
        const querySnapshot = await getDocs(collection(db, 'products', category, 'items'));
        const productData = querySnapshot.docs.map(docSnapshot => {
          const data = docSnapshot.data();
          const id = docSnapshot.id;
          const slug = generateProductSlug({ id, ...data });
          
          if (!data.slug || data.slug !== slug) {
            updateDoc(doc(db, 'products', category, 'items', id), { slug })
              .catch(err => console.warn(`Slug update failed for ${category}:`, err));
          }
          
          return {
            id,
            category: category,
            ...data,
            slug
          };
        });
        allProductData.push(...productData);
      }
      
      setProducts(allProductData);
    } catch (err) {
      setError('Failed to load products');
      console.error('Error fetching products:', err);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...products];

    // Apply filters
    if (filters.category) {
      filtered = filtered.filter(product => product.category === filters.category);
    }
    if (filters.brand) {
      filtered = filtered.filter(product => product.brand === filters.brand);
    }
    if (filters.priceRange) {
      const [min, max] = filters.priceRange.split('-').map(Number);
      filtered = filtered.filter(product => {
        const price = product.price;
        if (max) return price >= min && price <= max;
        return price >= min;
      });
    }

    // Apply sorting
    switch (sortBy) {
      case 'price-low':
        filtered.sort((a, b) => a.price - b.price);
        break;
      case 'price-high':
        filtered.sort((a, b) => b.price - a.price);
        break;
      case 'newest':
        filtered.sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));
        break;
      case 'discount':
        filtered.sort((a, b) => (b.discount || 0) - (a.discount || 0));
        break;
      default:
        // Featured - keep original order
        break;
    }

    setFilteredProducts(filtered);
  };

  const handleFilterChange = (filterType, value) => {
    setFilters(prev => ({
      ...prev,
      [filterType]: value
    }));
  };

  const clearFilters = () => {
    setFilters({
      brand: '',
      priceRange: '',
      category: ''
    });
    setSelectedCategory('');
    setSelectedBrand('');
    // Clear URL parameters
    navigate('/all-products', { replace: true });
  };

  const handleProductClick = (product) => {
    const slug = generateProductSlug(product);
    const pathPrefix = getProductPathPrefix(product.productType || product.category);
    navigate(`/${pathPrefix}/${slug || product.id}`);
  };

  const handleAddToCart = (product) => {
    const cartItem = {
      id: product.id,
      name: product.name,
      brand: product.brand,
      price: product.price, // Use product.price from AllProductsPage data
      originalPrice: product.originalPrice,
      imageUrls: product.imageUrls, // Use imageUrls like LaptopsPage
      // Note: AllProductsPage might have varied product data, so generalize features if necessary
      features: [
        product.processor,
        product.ramSize,
        product.storageSize ? `${product.storageSize} ${product.storageType}` : null,
        product.screenSize,
        product.model
      ].filter(Boolean),
      quantity: 1
    };
    addToCart(cartItem);

    // Play sound effect
    soundEffects.playAddToCartSound();
  };

  // Get unique filter options
  const getUniqueValues = (key) => {
    return [...new Set(products.map(product => product[key]).filter(Boolean))];
  };

  return (
    <div className="laptops-page">
      <Products2Hero 
        className="hero-tight"
        tagline="EXPLORE OUR COMPLETE RANGE OF CERTIFIED REFURBISHED DEVICES"
        title1="All Products"
        title2=""
        title3=""
        description=""
        button1Text="Explore All"
        button1Link="#laptops-collection"
      />

      <div className="container" id="laptops-collection">
        {loading ? (
          <div className="loading-container">
            <div className="loading-spinner"></div>
            <p>Loading products...</p>
          </div>
        ) : error ? (
          <div className="error-container">
            <p>{error}</p>
          </div>
        ) : (
          <>
            <div className="filter-section">
              <div className="filter-header">
                <div className="results-info">
                  <h2>
                    Showing {filteredProducts.length} {selectedBrand ? selectedBrand : selectedCategory ? selectedCategory.toLowerCase() : ''} products
                  </h2>
                  <p>
                    {selectedBrand 
                      ? `Perfect ${selectedBrand} devices for your needs`
                      : selectedCategory 
                        ? `Find your perfect ${selectedCategory.toLowerCase()} device`
                        : 'Browse our complete product collection'
                    }
                  </p>
                </div>
                <div className="filter-controls">
                  <button 
                    className={`filter-toggle ${showFilters ? 'active' : ''}`}
                    onClick={() => setShowFilters(!showFilters)}
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M10 18h4v-2h-4v2zM3 6v2h18V6H3zm3 7h12v-2H6v2z"/>
                    </svg>
                    Filters
                  </button>
                  <select 
                    value={sortBy} 
                    onChange={(e) => setSortBy(e.target.value)}
                    className="sort-select"
                  >
                    <option value="featured">Featured</option>
                    <option value="price-low">Price: Low to High</option>
                    <option value="price-high">Price: High to Low</option>
                    <option value="newest">Newest First</option>
                    <option value="discount">Best Deals</option>
                  </select>
                </div>
              </div>

              <div className={`filter-panel ${showFilters ? 'show' : ''}`}>
                <div className="filter-grid">
                  <div className="filter-group">
                    <label>Category</label>
                    <select 
                      value={filters.category} 
                      onChange={(e) => handleFilterChange('category', e.target.value)}
                    >
                      <option value="">All Categories</option>
                      {getUniqueValues('category').map(category => (
                        <option key={category} value={category}>{category}</option>
                      ))}
                    </select>
                  </div>

                  <div className="filter-group">
                    <label>Brand</label>
                    <select 
                      value={filters.brand} 
                      onChange={(e) => handleFilterChange('brand', e.target.value)}
                    >
                      <option value="">All Brands</option>
                      {getUniqueValues('brand').map(brand => (
                        <option key={brand} value={brand}>{brand}</option>
                      ))}
                    </select>
                  </div>

                  <div className="filter-group">
                    <label>Price Range</label>
                    <select 
                      value={filters.priceRange} 
                      onChange={(e) => handleFilterChange('priceRange', e.target.value)}
                    >
                      <option value="">All Prices</option>
                      <option value="0-25000">Under ₹25,000</option>
                      <option value="25000-50000">₹25,000 - ₹50,000</option>
                      <option value="50000-75000">₹50,000 - ₹75,000</option>
                      <option value="75000-100000">₹75,000 - ₹1,00,000</option>
                      <option value="100000">Above ₹1,00,000</option>
                    </select>
                  </div>

                  <div className="filter-actions">
                    <button onClick={clearFilters} className="clear-filters">
                      Clear All
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div className="laptops-grid">
              {filteredProducts.map((product) => (
                <div key={product.id} className="laptop-card-wrapper">
                  <ProductCard
                    product={{
                      image: product.imageUrls?.[0] || '/api/placeholder/300/200',
                      imageAlt: product.name,
                      badge: product.availability === 'Available' ? 'In Stock' : 'Out of Stock',
                      isNew: false,
                      stockLeft: product.stockLeft,
                      brand: product.brand || 'NexaWave',
                      name: product.name,
                      subtitle: [product.condition, product.warranty].filter(Boolean).join(' | '),
                      scentNote: [
                        product.processor,
                        product.ramSize,
                        product.storageSize ? `${product.storageSize} ${product.storageType || ''}`.trim() : '',
                        product.screenSize,
                        product.model
                      ].filter(Boolean).join(' | '),
                      rating: Number(product.rating) || 4.8,
                      reviews: Number(product.reviews) || 120,
                      originalPrice: Number(product.originalPrice) || Number(product.price) || 0,
                      salePrice: Number(product.price) || Number(product.originalPrice) || 0
                    }}
                    onCardClick={() => handleProductClick(product)}
                    onAddToCart={() => handleAddToCart(product)}
                    canAddToCart={product.availability === 'Available'}
                    showRating={false}
                    titleTwoLine={true}
                  />
              </div>
            ))}
          </div>

            {filteredProducts.length === 0 && (
              <div className="no-results">
                <div className="no-results-icon">🔍</div>
                <h3>No products found</h3>
                <p>Try adjusting your filters to see more results</p>
                <button onClick={clearFilters} className="btn btn-primary">
                  Clear Filters
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default AllProductsPage;
